import showToast from "hooks/useCustomToast";
import { ArtifactProto, ChatRequest, ChatResponse, ChatClientType, chatClientTypeToJSON, ExecutionStepProto } from "build-ts/chat_session";
import { DarwinHeaderList, SortBy, sortByToJSON, SortOrder, sortOrderToJSON } from "build-ts/common";

/**
 * Continue a chat session.
 * This is used to continue a chat session with a new message.
 *
 * @param sessionId - The session ID
 * @param message - The message to continue the chat session
 * @param autoSave - Whether to save message in the database automatically
 * @returns The chat response
 */
export const continueSession = async (
  sessionId: string,
  message: string,
  autoSave: boolean = true,
  userId: string,
): Promise<ChatResponse> => {
  try {
    const chatRequest = ChatRequest.create({
      session_header: { id: sessionId, author_id: userId },
      message: message,
      is_save: autoSave,
    });
    const response = await fetch(`${process.env.REACT_APP_CHAT_MESSAGE}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(chatRequest)),
    });

    if (!response.ok) {
      showToast("Failed to continue session", "", "error");
      throw new Error(`Failed to continue session: HTTP ${response.status}`);
    }
    const data = await response.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error continuing session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Save a chat session.
 * This is used to save a chat session to the database.
 *
 * @param sessionId - The session ID
 * @param title - Optional title to set before saving
 * @param description - Optional description to set before saving
 * @returns The response from the server. If the request is successful, the response will be a 200 status code.
 * @throws {Error} Throws an error if the request fails
 */
export const saveSession = async (sessionId: string, title?: string, description?: string): Promise<Response> => {
  try {
    let url = `${process.env.REACT_APP_SAVE_CHAT_SESSION}?session_id=${encodeURIComponent(sessionId)}`;
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (description) {
      url += `&description=${encodeURIComponent(description)}`;
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to save chat session", "", "error");
      throw new Error(`Failed to save chat session: HTTP ${response.status}`);
    }
    showToast("Successfully saved chat session", "", "success");
    return response;
  } catch (error: any) {
    showToast("Error saving chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Continue a chat session with streaming enabled.
 *
 * The server sends Server-Sent Events (SSE). Each event is either:
 *   - A plain-text token  → onToken is called with that string.
 *   - A JSON ChatResponse → onChatResponseChunk is called (carries REASONING artifacts or a
 *     structured RESPONSE from a response tool).
 *
 * The returned Promise resolves when the stream closes.
 *
 * @param sessionId - The session ID
 * @param message - The user message
 * @param autoSave - Whether to auto-save after execution
 * @param userId - The requesting user ID
 * @param onToken - Called with each plain-text token as it arrives
 * @param onChatResponseChunk - Called with each ChatResponse JSON chunk (reasoning / response-tool)
 */
export const continueSessionStreaming = async (
  sessionId: string,
  message: string,
  autoSave: boolean = true,
  userId: string,
  onToken: (token: string) => void,
  onChatResponseChunk: (chunk: ChatResponse) => void,
  onExecutionStep?: (step: ExecutionStepProto) => void,
): Promise<void> => {
  try {
    const chatRequest = ChatRequest.create({
      session_header: { id: sessionId, author_id: userId },
      message,
      is_save: autoSave,
      stream_response: true,
    });

    const fetchResponse = await fetch(`${process.env.REACT_APP_CHAT_MESSAGE}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(chatRequest)),
    });

    if (!fetchResponse.ok) {
      showToast("Failed to continue session", "", "error");
      return;
    }

    const reader = fetchResponse.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are delimited by \n\n
      const events = buffer.split("\n\n");
      buffer = events.pop() ?? "";

      for (const event of events) {
        // The backend JSON-encodes every token, so data is always a single-line
        // JSON value — no literal newlines can appear inside it.  Simply take the
        // first (and only) line of the event and strip the "data: " prefix.
        const line = event.includes("\n") ? event.split("\n")[0] : event;

        let data: string;
        if (line.startsWith("data: ")) {
          data = line.slice(6);
        } else if (line.startsWith("data:")) {
          data = line.slice(5);
        } else {
          continue;
        }

        if (!data) continue;
        if (data === "\"\"") continue;

        // Every SSE event is now JSON-encoded on the backend:
        //   - Text tokens    → JSON string  e.g. "Hello\\nworld"
        //   - ExecutionStep  → JSON object  with "timestamp_ms" field
        //   - ChatResponse   → JSON object  with "messages" / "session_header" fields
        // We distinguish object types by their characteristic fields.
        try {
          const parsed = JSON.parse(data);
          if (typeof parsed === "string") {
            // JSON-encoded LLM text token — correct newlines already restored by JSON.parse
            if (parsed) onToken(parsed);
          } else if (parsed !== null && typeof parsed === "object") {
            if ("timestamp_ms" in parsed) {
              // ExecutionStep — status update from the execution engine; deserialize enums properly
              onExecutionStep?.(ExecutionStepProto.fromJSON(parsed));
            } else {
              // ChatResponse — final response / reasoning chunk
              onChatResponseChunk(ChatResponse.fromJSON(parsed));
            }
          } else {
            onToken(String(parsed));
          }
        } catch {
          // Fallback: non-JSON string
          onToken(data);
        }

      }
    }
  } catch (error: any) {
    showToast("Error continuing session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Initialize a new chat session.
 * This is used to initialize a chat session
 *
 * @param request - The chat request
 * @returns The chat response
 */
export const initializeChatSession = async (request: ChatRequest): Promise<ChatResponse> => {
  try {
    const chatResponse = await fetch(`${process.env.REACT_APP_CHAT_MESSAGE}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(request))
    });
    if (!chatResponse.ok) {
      showToast("Failed to initialize chat session", "", "error");
      throw new Error(`Failed to initialize chat session: HTTP ${chatResponse.status}`);
    }
    const data = await chatResponse.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error initializing chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Fetch a chat session.
 * This is used to fetch all messages in a chat session.
 *
 * @param sessionId - The session ID
 * @param initializeTransaction - Whether to initialize the transaction in the stateful store for continuation
 * @returns The chat messages
 * @throws {Error} Throws an error if the request fails
 */
export const fetchChatSession = async (sessionId: string, initializeTransaction: boolean = false, includeMessages: boolean = true): Promise<ChatResponse> => {
  try {
    const url = `${process.env.REACT_APP_CHAT_SESSION}/${encodeURIComponent(sessionId)}?initialize_transaction=${initializeTransaction}&include_messages=${includeMessages}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Failed to fetch chat session", "", "error");
      throw new Error(`Failed to fetch chat session: HTTP ${response.status}`);
    }
    const data = await response.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error fetching chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};


/**
 * Fetch the list of all chat sessions.
 *
 * @param {ChatClientType[]} clientType - Filter by client type (required).
 * @param {number} limit - Maximum number of sessions to return (1-100).
 * @param {number} offset - Number of sessions to skip for pagination.
 * @param {string} titleSearch - Optional search string to filter by title.
 * @param {string} agentId - Optional filter by agent ID.
 * @param {Header[]} associatedEntityKey - Optional filter by associated entity headers.
 * @param {SortBy} sortBy - Field to sort by (default: LAST_MODIFIED_DATE).
 * @param {SortOrder} sortOrder - Sort order (default: DESC).
 * @returns {Promise<DarwinHeaderList>} A promise that resolves to DarwinHeaderList containing headers and total count.
 * @throws {Error} Throws an error if the request fails
 */
export const fetchListOfChatSessions = async (
  clientType: ChatClientType[] = [ChatClientType.FRIDA],
  limit: number = 50,
  offset: number = 0,
  titleSearch?: string,
  agentId?: string,
  associatedEntityKey?: string[],
  sortBy: SortBy = SortBy.LAST_MODIFIED_DATE,
  sortOrder: SortOrder = SortOrder.DESC,
  userId?: string,
): Promise<DarwinHeaderList> => {
  try {
    const queryParams = new URLSearchParams({
      client_type: JSON.stringify(clientType.map((clientType: ChatClientType) => chatClientTypeToJSON(clientType))),
      limit: limit.toString(),
      offset: offset.toString(),
      sort_by: sortByToJSON(sortBy),
      sort_order: sortOrderToJSON(sortOrder),
    });

    // Add optional parameters
    if (titleSearch && titleSearch.trim()) {
      queryParams.append("title_search", titleSearch.trim());
    }

    if (agentId && agentId.trim()) {
      queryParams.append("agent_id", agentId.trim());
    }

    if (associatedEntityKey) {
      queryParams.append("associated_entity_key", JSON.stringify(associatedEntityKey));
    }
    if (userId) {
      queryParams.append("user_id", userId);
    }

    const response = await fetch(`${process.env.REACT_APP_LIST_CHAT_SESSIONS}?${queryParams}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to fetch list of chat sessions", "", "error");
      // Return empty DarwinHeaderList on error
      return DarwinHeaderList.fromJSON({ headers: [], total: 0 });
    }

    const data = await response.json();
    return DarwinHeaderList.fromJSON(data);

  } catch (error: any) {
    showToast("Error fetching list of chat sessions", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};


/**
 * Delete a chat session.
 *
 * @param sessionId - The session ID
 * @throws {Error} Throws an error if the request fails
 */
export const deleteChatSession = async (sessionId: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_CHAT_SESSION}/${sessionId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Failed to delete chat session", "", "error");
      throw new Error(`Failed to delete chat session: HTTP ${response.status}`);
    }
    showToast("Chat session deleted successfully", "", "success");
    return response;
  } catch (error: any) {
    showToast("Error deleting chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Update chat session headers (title and/or visibility) in Darwin.
 *
 * @param {string[]} sessionIds - Array of chat session IDs to update (required, cannot be empty).
 * @param {string} [title] - New title for the session (optional, requires exactly 1 session ID).
 * @param {string} [description] - Chat session description (optional, requires exactly 1 session ID).
 * @param {boolean} [isHidden] - Whether the sessions should be hidden (optional, applies to all session IDs).
 * @returns {Promise<any | null>} A promise that resolves to the update result from the server.
 * @throws {Error} Throws an error if there is an issue updating chat session headers or if the request fails.
 */
export const updateChatSessionHeaders = async (
  sessionIds: string[],
  title?: string,
  description?: string,
  isHidden?: boolean,
): Promise<boolean> => {
  try {
    const queryParams = new URLSearchParams({
      session_ids: JSON.stringify(sessionIds),
    });

    if (title !== undefined) {
      queryParams.append("title", title);
    }

    if (description !== undefined) {
      queryParams.append("description", description);
    }

    if (isHidden !== undefined) {
      queryParams.append("is_hidden", isHidden.toString());
    }

    const response = await fetch(
      `${process.env.REACT_APP_UPDATE_CHAT_HEADERS}?${queryParams}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      showToast("Failed to update chat session headers", "", "error");
      return false;
    }

    // Backend returns 204 No Content on success
    return true;
  } catch (error) {
    showToast("Failed to update chat session headers", "", "error");
    return false;
  }
};

export interface DuplicateSessionParams {
  originalSessionId: string;
  currentUserId: string;
  clientType: string;
  save?: boolean;
  initTransaction?: boolean;
}

/**
 * Duplicate a chat session into a new independent session.
 */
export const duplicateChatSession = async (
  params: DuplicateSessionParams,
): Promise<ChatResponse | null> => {
  const { originalSessionId, currentUserId, clientType, save = true, initTransaction = false } = params;
  const query = new URLSearchParams({
    original_session_id: originalSessionId,
    current_user_id: currentUserId,
    client_type: clientType,
    save: String(save),
    init_transaction: String(initTransaction),
  });
  try {
    const response = await fetch(`${process.env.REACT_APP_DUPLICATE_CHAT_SESSION}?${query}`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to duplicate session", "", "error");
      throw new Error("Failed to duplicate session");
    }

    const data = await response.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error duplicating session", error?.message || "Unknown error occurred", "error");
    return null;
  }
};


/**
 * List job artifacts filtered by artifact type.
 *
 * @param artifactType - ArtifactType enum name (required, e.g. "ANALYSIS_OUTPUT")
 * @param jobId - ScheduleJob ID (optional; only sent when artifactType is ANALYSIS_OUTPUT)
 * @returns Array of ArtifactProto, newest first
 */
export const listJobArtifacts = async (
  artifactType: string,
  jobId?: string,
): Promise<ArtifactProto[]> => {
  try {
    const params: Record<string, string> = { artifact_type: artifactType };
    if (artifactType === "ANALYSIS_OUTPUT" && jobId) {
      params.job_id = jobId;
    }
    const query = new URLSearchParams(params);
    const response = await fetch(`${process.env.REACT_APP_LIST_JOB_ARTIFACTS}?${query}`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Failed to fetch artifacts", "", "error");
      return [];
    }
    const data = await response.json();
    return data.map((item: ArtifactProto) => ArtifactProto.fromJSON(item));
  } catch (error: any) {
    showToast("Error fetching artifacts", error?.message || "Unknown error occurred", "error");
    return [];
  }
};

/**
 * Refresh the system prompt for a chat session.
 * 
 * @ param request - The chat request
 * @ returns The chat response
 */
export const refreshSystemPrompt = async (request: ChatRequest): Promise<Response> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_REFRESH_SYSTEM_PROMPT}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(request)),
    });
    if (!response.ok) {
      showToast("Failed to refresh system prompt", "", "error");
      throw new Error("Failed to refresh system prompt");
    }
    return response;
  } catch (error: any) {
    showToast("Error refreshing system prompt", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};
